<script setup lang="ts">
import { asLink } from '@prismicio/client'

const navigation = useNavigation()
const settings = useSettings()
const route = useRoute()

function linkUrl(field: unknown): string {
  return (asLink(field as Parameters<typeof asLink>[0]) as string) || ''
}

/**
 * A nav link matches the current page when it's an exact hit or the current
 * path is nested beneath it (e.g. `/vacatures` matches `/vacatures/monteur`).
 * Home (`/`) only matches exactly so it isn't active everywhere.
 */
function linkMatches(url: string): boolean {
  if (!url) return false
  const path = route.path.replace(/\/$/, '') || '/'
  const target = url.replace(/\/$/, '') || '/'
  if (target === '/') return path === '/'
  return path === target || path.startsWith(target + '/')
}

function isChildActive(child: { link: unknown }): boolean {
  return linkMatches(linkUrl(child.link))
}

/** A parent is active when it matches, or when any of its children match. */
function isItemActive(item: { link: unknown; children: Array<{ link: unknown }> }): boolean {
  return linkMatches(linkUrl(item.link)) || item.children.some(isChildActive)
}

const navTree = computed(() => {
  const items = navigation.value?.data.links || []
  const tree: Array<{ label: any; link: any; children: Array<{ label: any; link: any }> }> = []

  for (const item of items) {
    if (item.is_child && tree.length > 0) {
      tree[tree.length - 1].children.push({
        label: item.label,
        link: item.link
      })
    } else {
      tree.push({
        label: item.label,
        link: item.link,
        children: []
      })
    }
  }

  return tree
})

const isMobileMenuOpen = ref(false)
const openMobileIndex = ref<number | null>(null)

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  if (!isMobileMenuOpen.value) openMobileIndex.value = null
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false
  openMobileIndex.value = null
}

function toggleMobileItem(index: number) {
  openMobileIndex.value = openMobileIndex.value === index ? null : index
}

watch(isMobileMenuOpen, (open) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = open ? 'hidden' : ''
})

watch(() => route.fullPath, () => closeMobileMenu())

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') closeMobileMenu()
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
  }
})
</script>

<template>
  <Bounded
    as="header"
    y-padding="sm"
    class="pa-header"
  >
    <div class="pa-header__inner">
      <div class="pa-header__brand">
        <NuxtLink
          to="/"
          class="pa-header__logo"
        >
          <PrismicImage :field="settings?.data.website_logo" :width="280" />
        </NuxtLink>

        <button
            type="button"
            class="pa-header__burger"
            :aria-expanded="isMobileMenuOpen"
            aria-label="Menu openen"
            aria-controls="pa-mobile-menu"
            @click="toggleMobileMenu"
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </div>

      <nav class="pa-nav">
        <div
            v-for="(item, index) in navTree"
            :key="index"
            class="pa-nav__item"
            :class="{
              'pa-nav__item--has-children': item.children.length > 0,
              'pa-nav__item--active': isItemActive(item),
            }"
        >
          <PrismicLink
              :field="item.link"
              class="pa-nav__link"
          >
            <PrismicText :field="item.label" />
            <svg
                v-if="item.children.length > 0"
                class="pa-nav__chevron"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </PrismicLink>

          <div
              v-if="item.children.length > 0"
              class="pa-nav__dropdown"
              :class="item.children.length >= 6 ? 'pa-nav__dropdown--cols-2' : 'pa-nav__dropdown--cols-1'"
          >
            <PrismicLink
                v-for="(child, childIndex) in item.children"
                :key="childIndex"
                :field="child.link"
                class="pa-nav__dropdown-link"
                :class="{ 'pa-nav__dropdown-link--active': isChildActive(child) }"
            >
              <PrismicText :field="child.label" />
            </PrismicLink>
          </div>
        </div>
      </nav>

      <div class="pa-header__actions">
        <a
            v-if="settings?.data.phone"
            :href="`tel:${settings.data.phone}`"
            class="pa-header__phone"
        >
          <span class="pa-header__phone-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </span>
          <span class="pa-header__phone-text">{{ settings.data.phone }}</span>
        </a>

        <NuxtLink
            to="/contact"
            class="pa-btn pa-btn--primary pa-header__cta"
        >
          Contact
        </NuxtLink>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="pa-mobile-menu">
        <div
            v-if="isMobileMenuOpen"
            id="pa-mobile-menu"
            class="pa-mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Hoofdmenu"
        >
          <div class="pa-mobile-menu__top">
            <NuxtLink to="/" class="pa-mobile-menu__logo" @click="closeMobileMenu">
              <PrismicImage :field="settings?.data.website_logo" :width="200" />
            </NuxtLink>
            <button
                type="button"
                class="pa-mobile-menu__close"
                aria-label="Menu sluiten"
                @click="closeMobileMenu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <nav class="pa-mobile-menu__nav">
            <div class="pa-mobile-menu__item">
              <NuxtLink
                  to="/"
                  class="pa-mobile-menu__row"
                  @click="closeMobileMenu"
              >
                <span class="pa-mobile-menu__label">Homepage</span>
                <svg class="pa-mobile-menu__arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </NuxtLink>
            </div>
            <div
                v-for="(item, index) in navTree"
                :key="index"
                class="pa-mobile-menu__item"
                :class="{ 'pa-mobile-menu__item--open': openMobileIndex === index }"
            >
              <button
                  v-if="item.children.length > 0"
                  type="button"
                  class="pa-mobile-menu__row"
                  :class="{ 'pa-mobile-menu__row--active': isItemActive(item) }"
                  :aria-expanded="openMobileIndex === index"
                  @click="toggleMobileItem(index)"
              >
                <span class="pa-mobile-menu__label">
                  <PrismicText :field="item.label" />
                </span>
                <svg class="pa-mobile-menu__arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <PrismicLink
                  v-else
                  :field="item.link"
                  class="pa-mobile-menu__row"
                  :class="{ 'pa-mobile-menu__row--active': isItemActive(item) }"
                  @click="closeMobileMenu"
              >
                <span class="pa-mobile-menu__label">
                  <PrismicText :field="item.label" />
                </span>
                <svg class="pa-mobile-menu__arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </PrismicLink>

              <div
                  v-if="item.children.length > 0"
                  v-show="openMobileIndex === index"
                  class="pa-mobile-menu__children"
              >
                <PrismicLink
                    v-for="(child, childIndex) in item.children"
                    :key="childIndex"
                    :field="child.link"
                    class="pa-mobile-menu__child-row"
                    :class="{ 'pa-mobile-menu__child-row--active': isChildActive(child) }"
                    @click="closeMobileMenu"
                >
                  <span>
                    <PrismicText :field="child.label" />
                  </span>
                  <svg class="pa-mobile-menu__arrow pa-mobile-menu__arrow--sm" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </PrismicLink>
              </div>
            </div>
          </nav>

          <div class="pa-mobile-menu__footer">
            <NuxtLink
                to="/"
                class="pa-btn pa-btn--primary pa-mobile-menu__cta"
                @click="closeMobileMenu"
            >
              Vrijblijvend advies
            </NuxtLink>
          </div>
        </div>
      </Transition>
    </Teleport>
  </Bounded>
</template>
